import { ReactElement, ReactNode } from 'react';
import * as Immutable from 'immutable';
import { SVGProps } from '@components/Icons/SVGWrapper';
import Contact from '@models/Contact';
import { GroupViewState } from '@reducers/ui/groupView';
import SearchResults from '@models/SearchResults';
import { Emitter } from 'mitt';
import { Location } from 'react-router-dom';
import { ArchiveViewState } from '@reducers/ui/archiveView';
import { DeviceIntegrationProvider } from '@models/Integration';

export type ID = string;
export interface IDRecord {
  id: ID;
}

export type ClientEnv = 'Web' | 'Mac' | 'Windows' | 'iOS';

export interface ReduxAction {
  type: string;
  payload?: any;
  error?: boolean;
}

export interface UserAction {
  title: ReactNode;
  type: string;
  action: (args?: any) => void;
  icon?: (props: SVGProps) => ReactElement<any, any>;
  inlineIcon?: (props: SVGProps) => ReactElement<any, any>;
  disabled?: boolean;
  synonyms?: Array<string>;
  keyboard?: string;
  className?: string;
  subtitle?: ReactNode;
}

// Search types
export type SearchType =
  | 'search'
  | 'adHocSearch'
  | 'mentionSearch'
  | 'allPeopleSearch'
  | 'groupSearch';

export type TipKey =
  | 'interest'
  | 'org'
  | 'location'
  | 'social_media'
  | 'date'
  | 'relationship'
  | 'education'
  | 'content_type'
  | 'upcoming_birthdays'
  | 'cleanups'
  | 'all';

export type SearchSortBy =
  | 'closeness'
  | 'firstName'
  | 'lastName'
  | 'firstInteraction'
  | 'created'
  | 'lastInteraction';

export type SearchSortByField =
  | 'firstName'
  | 'lastName'
  | 'firstInteractionDate'
  | 'lastInteractionDate'
  | 'created'
  | 'score';

export type SearchSortDirection = 'asc' | 'desc';

export interface SearchSort {
  name?: SearchSortBy;
  direction?: SearchSortDirection;
}

export interface SearchState {
  type: SearchType;
  term: string;
  results: SearchResults;
  sorting: SearchSort;
  recent?: Array<any>;
  tips?: { [K in TipKey]: Array<string> };
  deletedIDCacheToExpire: Immutable.Set<ID>;
  resourceID?: ID;
  pauseLoadingMore?: boolean;
}

// Type for building UI around available user actions given the current app state
export interface UserActionState {
  selectedContact?: Contact;
  selectedContactBatch?: Immutable.Set<ID>;
  selectedContactArray?: Array<Contact>;
  groupView?: GroupViewState & { isShared: boolean };
  archiveView?: ArchiveViewState;
  isMultiselectView?: boolean;
  location?: Location;
}

type MenuOption = Partial<UserAction> & {
  divider?: boolean;
  suffix?: ReactNode;
  keepMenuOpenOnClick?: boolean;
  active?: boolean;
  button?: ReactNode;
  focusedClassName?: string;
};

export type DropdownMenuOption = MenuOption & {
  submenu?: MenuOption[];
};

export type ProductName = 'Clay Personal' | 'Clay Pro' | 'Clay Teams';

export interface IAPProduct {
  productIdentifier: string;
  formattedPrice: string;
  price: number;
  subscriptionPeriod: {
    numberOfUnits: number;
    unit: 'month' | 'year';
  };
  // ...other unused properties
}

export type IAPTransactionState =
  | 'purchasing'
  | 'purchased'
  | 'failed'
  | 'restored'
  | 'deferred'
  | 'finished';

export interface IAPTransactionEvent {
  transactionState: IAPTransactionState;
  errorMessage?: string;
  transactionIdentifier?: string;
  originalTransactionIdentifier?: string;
  payment: {
    productIdentifier: 'com.clay.membership.annual' | 'com.clay.membership.monthly';
  };
}

interface Entitlements {
  'contact.limit': number | null;
  'integrations.email': boolean;
  'search.internal': boolean;
  'nexus.enabled': boolean;
  'nexus.beta': boolean;
  'account.delete': boolean;
  'account.team_member': boolean;
  'additional.team.members': number;
  'search.full': boolean;
  'permissions.contractor': boolean;
  'permissions.admin': boolean;
  'permissions.staff': boolean;
  'linkedin.version': number;
  'groups.export': boolean;
  'contacts.edit.disabled': boolean;
}

export type EntitlementName = keyof Entitlements;

export type UserEntitlements = Partial<Entitlements>;

export type BillingProvider = 'Stripe' | 'AppSumo' | 'RevenueCat';
type SubscriptionStatus = 'active' | 'cancelled' | 'inactive';

export interface ClaySubscription {
  id: number;
  plan: {
    name: string;
    provider: BillingProvider;
    product: { name: ProductName };
  };
  status: SubscriptionStatus;
  deactivated: string | null;
  created: string;
  updated: string;
  trial: boolean;
  management_url: string;
}

export interface UserSession {
  primary_product: { name: ProductName };
  id: number;
  assistant_id?: number;
  algolia_restricted_search_key: string;
  avatar_options: Array<string>;
  avatar_url: string;
  typesense_search_key: string;
  contact_id: number;
  email: string;
  first_name: string;
  last_name: string;
  social_linkedin_url: string;
  social_twitter_url: string;
  username: string;
  zapier_key: string;
  has_completed_onboarding: boolean;
  upgrade_url?: string;
  // undefined means we're missing our entitlement (should not happen), null means we have no limit
  contactLimit?: number | null;
  isOverContactLimit?: boolean;
  billing_provider: BillingProvider;
  churnkey: string;
  stripe_customer_id: string;
}

export type UserProfileRequest = Partial<
  Omit<UserSession, 'avatar_url'> & { avatar_url: string | File }
>;

interface AccountsProgress {
  total: number;
  importing: number;
  processed: number;
}

interface EnrichmentProgress {
  total: number;
  processed: number;
}

export interface UserAccountProcessing {
  status: string;
  accounts: AccountsProgress;
  enrichment: EnrichmentProgress;
}

export type AlertType = 'loading' | 'success' | 'error' | 'system';

export type SystemAlertSeverity = 'low' | 'medium' | 'high';

export type ButtonVariant =
  | 'none'
  | 'flex'
  | 'small'
  | 'muted'
  | 'primary'
  | 'warning'
  | 'danger'
  | 'icon'
  | 'block'
  // NEW BUTTONS!
  | 'v2';

export interface SelectOption {
  label: string;
  value: string | number;
}

// In-app confirmation dialog types
export type ConfirmButtonConfig = React.HTMLProps<HTMLButtonElement> & {
  title: string;
  value: any;
  variant?: ButtonVariant;
};

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  action?: string;
  actions?: Array<ConfirmButtonConfig>;
  variant?: ButtonVariant;
  smallModal?: boolean;
  noCancelButton?: boolean;
  forceDarkMode?: boolean;
}

export type ConfirmCallback = (callbackValue: any) => void;

export type Confirm = (opts: ConfirmOptions, cb: ConfirmCallback) => void;

export type ContactInfoSource = 'BI' | 'CL' | 'EM' | 'LI' | 'MA' | 'ME' | 'SS' | 'TW' | 'FB';
export type ContactInfoType =
  | 'email'
  | 'linkedin'
  | 'manual'
  | 'phone'
  | 'messages'
  | 'twitter'
  | 'url'
  | 'facebook';

export interface ContactInfo {
  value: string;
  type?: ContactInfoType;
  source?: ContactInfoSource;
  label?: string;
}

export interface Coords {
  lat: number;
  lon: number;
}

export interface IntegrationState {
  enabled?: boolean;
  isSupported?: boolean;
  sync?: () => void;
  disconnect?: (action: 'delete' | 'archive') => Promise<void>;
  setup?: () => void;
}

export type GroupActionSource =
  | 'Search Menu'
  | 'Search Command Bar'
  | 'List'
  | 'Merge Modal'
  | 'Keyboard Shortcut';

export interface CustomAppProps {
  confirm: Confirm;
  isElectron: boolean;
}

export type AppSessionEvent = {
  'hotkey': KeyboardEvent;
  'hotkey_1': KeyboardEvent;
  'hotkey_2': KeyboardEvent;
  'keydown': KeyboardEvent;
  'keydown:all': KeyboardEvent;
  'shortcuts:open': void;
  'window:focus': FocusEvent;
};
export type AppSession = Emitter<AppSessionEvent> & CustomAppProps;

export enum DesktopUpdateStatus {
  checking = 'checking',
  downloading = 'downloading',
  unavailable = 'unavailable',
  ready = 'ready',
}

export enum DesktopTriggeredUIAction {
  showNewPersonView = 'show-new-person-view',
  showNewNoteView = 'show-new-note-view',
  toggleSidebar = 'toggle-sidebar',
  toggleNotesView = 'toggle-notes-view',
  showSearchView = 'show-search-view',
  showHomeView = 'show-home-view',
  showPeopleView = 'show-people-view',
  showNotesView = 'show-notes-view',
  showStarredView = 'show-starred-view',
  showNexusView = 'show-nexus-view',
  showKeyboardShortcuts = 'show-keyboard-shortcuts',
  showSettingsView = 'show-settings-view',
  showCommandBar = 'show-command-bar',
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      added: { added: string };
      removed: { removed: string };
      contacts: { contacts: string };
      events: { events: string };
      notes: { notes: string };
    }
  }
}

interface LinkedInEducationExperience {
  school: string;
  degree?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
}

interface LinkedInWorkExperience {
  company: string;
  role: string;
  startDate: string;
  endDate?: string;
  duration: string;
  description?: string;
}

export interface LinkedInProfile {
  username: string;
  firstName: string;
  lastName: string;
  headline: string;
  avatarUrl: string;
  location: string;
  country: string;
  bio: string;
  education: LinkedInEducationExperience[];
  experience: LinkedInWorkExperience[];
}

/**
 * Events passed via preload.js
 */
export enum IntegrationRequestEvent {
  requestSyncState = 'requestSyncState',
  requestSync = 'requestSync',
  isAuthorized = 'isAuthorized',
  checkDiskAccess = 'checkDiskAccess',
  requestAuthorization = 'requestAuthorization',
  requestDiskAccess = 'requestDiskAccess',
  remove = 'remove',
}

export type SupportedIntegration = {
  platform: DeviceIntegrationProvider;
  requiresAuth?: boolean;
};

export enum DesktopIntegrationStatusEventType {
  startingSync = 'startingSync',
  completedSync = 'completedSync',
  progress = 'progress',
  auth = 'auth',
  removed = 'removed',
  error = 'error',
  aborted = 'aborted',
}

export type DesktopIntegrationStatusEvent =
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.startingSync;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.completedSync;
      total: number;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.progress;
      complete: number;
      total: number;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.auth;
      authed: boolean;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.removed;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.aborted;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.error;
      error?: string;
      type?: string;
    };

export enum DesktopProfileUpdateStatusEventType {
  profileUpdateError = 'profileUpdateError',
  profileUpdateCompleted = 'profileUpdateCompleted',
  linkedInNeedsAuth = 'linkedInNeedsAuth',
}

export type DesktopProfileUpdateStatusEvent =
  | {
      contactID: ID;
      status: DesktopProfileUpdateStatusEventType.profileUpdateError;
      error: string;
    }
  | {
      contactID: ID;
      status: DesktopProfileUpdateStatusEventType.profileUpdateCompleted;
      profile: LinkedInProfile;
      isPartial: boolean;
    }
  | {
      contactID: ID;
      status: DesktopProfileUpdateStatusEventType.linkedInNeedsAuth;
    };

export type IntegrationStatusEventCallback = (
  event: DesktopIntegrationStatusEvent | DesktopProfileUpdateStatusEvent,
) => void;

export type UnsubscribeFunction = () => void;
